import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Dialogs use the native <dialog> element so focus trapping, Escape, and
 * inertness of the page behind come from the platform rather than from
 * hand-rolled key handlers.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  width = 'md',
  lang,
  dir,
  className = '',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  width?: 'sm' | 'md' | 'lg';
  lang?: string;
  dir?: 'ltr' | 'rtl';
  className?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  if (typeof document === 'undefined') return null;

  const widths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' };

  return createPortal(
    // A native <dialog> already closes on Escape via onCancel, so keyboard
    // users are served. This click is the backdrop, which has no keyboard
    // equivalent to pair it with.
    // biome-ignore lint/a11y/useKeyWithClickEvents: explained above
    <dialog
      ref={ref}
      lang={lang}
      dir={dir}
      onClose={onClose}
      onCancel={(event) => {
        // The owner may need to keep an unsaved or pending form open.
        event.preventDefault();
        onClose();
      }}
      // Clicking the backdrop (the dialog element itself, outside the panel)
      // closes it; clicks inside the panel stop here.
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
      className={`m-auto w-[calc(100%-2rem)] ${widths[width]} ${className} rounded-lg border border-rule bg-surface p-0 text-ink shadow-[var(--shadow-overlay)] backdrop:bg-black/40`}
    >
      <div className="border-b border-rule px-5 py-4">
        <h2 className="text-base font-semibold text-ink">{title}</h2>
        {description && <p className="mt-1 text-xs text-ink-3">{description}</p>}
      </div>
      {children && <div className="px-5 py-4">{children}</div>}
      {footer && (
        <div className="flex justify-end gap-2 border-t border-rule bg-surface-2 px-5 py-3">
          {footer}
        </div>
      )}
    </dialog>,
    document.body,
  );
}

/**
 * Confirmation for actions that change someone else's account. `danger`
 * styles the confirm button; `requireReason` makes the audit-log reason
 * mandatory in the UI even where the API accepts it as optional — an
 * unexplained entry is far less useful six months later.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  danger = false,
  pending = false,
  reason,
  onReasonChange,
  requireReason = false,
  reasonLabel = 'Reason',
  reasonPlaceholder,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  danger?: boolean;
  pending?: boolean;
  reason?: string;
  onReasonChange?: (value: string) => void;
  requireReason?: boolean;
  reasonLabel?: string;
  reasonPlaceholder?: string;
}) {
  const needsReason = requireReason && !(reason ?? '').trim();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      width="sm"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}
            onClick={onConfirm}
            disabled={pending || needsReason}
          >
            {pending ? 'Working…' : confirmLabel}
          </button>
        </>
      }
    >
      {onReasonChange && (
        <label className="block">
          <span className="label">
            {reasonLabel}
            {requireReason ? '' : ' (optional)'}
          </span>
          <textarea
            className="textarea"
            rows={3}
            value={reason ?? ''}
            placeholder={reasonPlaceholder}
            onChange={(e) => onReasonChange(e.target.value)}
          />
          <span className="mt-1.5 block text-xs text-ink-3">
            Recorded in the audit log against your name.
          </span>
        </label>
      )}
    </Modal>
  );
}
